import { Card, Badge, Collapse, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFileAlt,
  faCalendarAlt,
  faTag,
  faUser,
  faClipboard,
  faCaretDown,
  faCaretUp,
  faCheck,
  faTimes,
  faMapMarkerAlt,
  faBuilding,
  faInfo,
  faImage,
  faCloud
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { useState } from "react";
import { documentEnums } from "../../../helpers/enumHelpers";
import { DocumentBase, Phase } from "types";

type Props = {
  document: DocumentBase;
  phase: Phase;
};

const phaseColors = {
  [Phase.FB]: {
    bg: "#DEEBF5",
    text: "#1E40AF",
    border: "#93C5FD"
  },
  [Phase.MB]: {
    bg: "#E1EBDD",
    text: "#166534",
    border: "#86EFAC"
  },
  [Phase.EB]: {
    bg: "#FED5D2",
    text: "#991B1B",
    border: "#FCA5A5"
  }
};

export const DocumentItem = ({ document, phase }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const phaseColor = phaseColors[phase];
  
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "yyyy-MM-dd HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const renderBooleanIcon = (value: boolean) => (
    value ? 
      <FontAwesomeIcon icon={faCheck} className="text-success" /> : 
      <FontAwesomeIcon icon={faTimes} className="text-danger" />
  );

  const DetailSection = ({ title, icon, children }: { title: string; icon: any; children: React.ReactNode }) => (
    <Card className="mb-3 border-0 shadow-sm rounded-3">
      <Card.Body className="p-3">
        <h6 className="mb-3 d-flex align-items-center fw-bold" style={{ color: phaseColor.text }}>
          <FontAwesomeIcon icon={icon} className="me-2" />
          {title}
        </h6>
        {children}
      </Card.Body>
    </Card>
  );

  const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <li className="mb-2 p-2 bg-white border rounded-3 d-flex align-items-center">
      <strong className="me-1">{label}:</strong> {value}
    </li>
  );

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card 
      className="mb-3 shadow-sm rounded-3" 
      style={{ 
        backgroundColor: phaseColor.bg,
        borderColor: phaseColor.border,
        borderWidth: "1px",
        borderStyle: "solid"
      }}
    >
      <Card.Body className="p-3">
        <div 
          className="d-flex justify-content-between align-items-start cursor-pointer"
          onClick={toggleExpand}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon 
              icon={faFileAlt} 
              className="fs-4"
              style={{ color: phaseColor.text }}
            />
            <div>
              <h6 className="mb-1 fw-bold" style={{ color: phaseColor.text }}>
                {document.name || "Unnamed Document"}
              </h6>
              <div className="d-flex align-items-center gap-2 text-muted small">
                <span className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                  {formatDate(document.createdDate)}
                </span>
                {document.createdBy && (
                  <span className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faUser} className="me-1" />
                    {document.createdBy}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-start gap-2">
            <div className="d-flex gap-2">
              <Badge 
                bg="white"
                className="py-1 px-2 rounded-pill d-flex align-items-center"
                style={{ color: phaseColor.text }}
              >
                <FontAwesomeIcon icon={faTag} className="me-1" />
                {document.documentType || "No Type"}
              </Badge>
              <Badge 
                bg="white"
                className="py-1 px-2 rounded-pill d-flex align-items-center"
                style={{ color: document.status === "Active" ? "#047857" : "#6B7280" }}
              >
                {document.status || "No Status"}
              </Badge>
            </div>
            <FontAwesomeIcon 
              icon={isExpanded ? faCaretUp : faCaretDown} 
              className="text-dark"
            />
          </div>
        </div>
        
        {document.remarks && (
          <div className="mt-3 p-2 bg-white bg-opacity-75 rounded-3 border border-light small">
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faClipboard} style={{ color: phaseColor.text }} />
              <span>{document.remarks}</span>
            </div>
          </div>
        )}

        <Collapse in={isExpanded}>
          <div className="mt-4">
            <Row className="g-3">
              {/* Column 1: Location Details and Property Features */}
              <Col md={6}>
                <DetailSection title="Location Details" icon={faMapMarkerAlt}>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                    {document.address && <DetailItem label="Address" value={document.address} />}
                    {document.number && <DetailItem label="Number" value={document.number} />}
                    {document.rise && <DetailItem label="Rise" value={document.rise} />}
                    {document.plan && <DetailItem label="Plan" value={document.plan} />}
                    {document.litteria && <DetailItem label="Litteria" value={document.litteria} />}
                    {document.evhgh && <DetailItem label="Evhgh" value={document.evhgh} />}
                  </ul>
                </DetailSection>

                <DetailSection title="Property Features" icon={faBuilding}>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                    <DetailItem label="Stairwell" value={renderBooleanIcon(document.stairwell)} />
                    <DetailItem label="First House" value={renderBooleanIcon(document.fristHouse)} />
                    <DetailItem label="Gen KV" value={renderBooleanIcon(document.gen_KV)} />
                    <DetailItem label="Local" value={renderBooleanIcon(document.local)} />
                  </ul>
                </DetailSection>
              </Col>

              {/* Column 2: Project & Basic Info and Building Materials */}
              <Col md={6}>
                <DetailSection title="Project & Basic Info" icon={faInfo}>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                    <DetailItem label="Project Number" value={document.projectNumber} />
                    {document.foreignId && (
                      <li className="mb-2 p-2 bg-white border rounded-3 d-flex align-items-center">
                        <Badge className="d-flex align-items-center py-1 px-2 bg-info text-white border border-dark rounded-pill">
                          <FontAwesomeIcon icon={faCloud} className="me-1" style={{ color: '#FFD700' }} />
                          <span>NCVIB-ID: {document.foreignId}</span>
                        </Badge>
                      </li>
                    )}
                    <DetailItem label="Phase" value={documentEnums.Phase.getName(document.phase)} />
                    <DetailItem label="Is Skadebesiktning" value={renderBooleanIcon(document.isSkadebesiktning)} />
                    <DetailItem label="PDF Done" value={renderBooleanIcon(document.isPdfDone || false)} />
                    <DetailItem label="Is Deleted" value={renderBooleanIcon(document.isDelete)} />
                  </ul>
                </DetailSection>

                <DetailSection title="Building Materials" icon={faBuilding}>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 small">
                    <DetailItem 
                      label="Marktyp Undergrund" 
                      value={documentEnums.MarktypUndergrund.getName(document.marktypUndergrund)} 
                    />
                    <DetailItem 
                      label="Grundlaggning" 
                      value={documentEnums.Grundlaggning.getName(document.grundlaggning)} 
                    />
                    <DetailItem 
                      label="Stommaterial" 
                      value={documentEnums.Stommaterial.getName(document.stommaterial)} 
                    />
                    <DetailItem 
                      label="Bjalklagsmaterial" 
                      value={documentEnums.Bjalklagsmaterial.getName(document.bjalklagsmaterial)} 
                    />
                    <DetailItem 
                      label="Fasadmaterial" 
                      value={documentEnums.Fasadmaterial.getName(document.fasadmaterial)} 
                    />
                    <DetailItem 
                      label="Brunn" 
                      value={documentEnums.Brunn.getName(document.brunn)} 
                    />
                    <DetailItem 
                      label="Murade Eldstader" 
                      value={documentEnums.MuradeEldstader.getName(document.muradeEldstader)} 
                    />
                    {document.kansligUtrustning !== undefined && (
                      <DetailItem 
                        label="Kanslig Utrustning" 
                        value={documentEnums.KansligUtrustning.getName(document.kansligUtrustning)} 
                      />
                    )}
                    <DetailItem 
                      label="Dimensionerande Byggnadsmaterial" 
                      value={documentEnums.DimmensionerandeByggnadsmaterial.getName(document.dimmensionerandeByggnadsmaterial)} 
                    />
                    {document.ovrigt !== undefined && (
                      <DetailItem 
                        label="Ovrigt" 
                        value={documentEnums.Ovrigt.getName(document.ovrigt)} 
                      />
                    )}
                  </ul>
                </DetailSection>
              </Col>

              {/* Full-width Column for Document Image */}
              {document.image && (
                <Col md={12}>
                  <DetailSection title="Document Image" icon={faImage}>
                    <div className="text-center">
                      <a href={document.image} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={document.image} 
                          alt="Document" 
                          className="img-fluid rounded-3 border shadow-sm" 
                          style={{ maxHeight: "300px", cursor: "pointer" }}
                        />
                      </a>
                    </div>
                  </DetailSection>
                </Col>
              )}
            </Row>
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};